<script setup>
    import { defineProps, computed, reactive, watch, onMounted, ref } from 'vue';
    import gsap from "gsap/gsap-core";

    const props = defineProps({
        value: {
            type: Number,
            default: 0,
        },
        minValue: {
            type: Number,
            default: 0,
        },
        maxValue: {
            type: Number,
            default: 100,
        },
        label: {
            type: String,
            default: '',
        },
        percentage: {
            type: Number,
            default: null,
        },
    });

    const initialRun = ref(false);

    const tweenedNumber = reactive({
        number: 0,
    });

    const calculatedPercentage = computed(() => {
        if (props.percentage) {
            return props.percentage;
        } else {
            return (((props.value - props.minValue) / (props.maxValue - props.minValue)) * 100).toFixed(2);;
        }
    });

    const displayValue = ref(100);

    const barStyledObject = computed(() => {
        if (initialRun.value) {
            initialRun.value = false;
            return 0;
        }

        const calc = Number(calculatedPercentage.value);
        return calc.toFixed(0) + '%';
    });

    const tweenToValue = () => {
        gsap.to(tweenedNumber, {
            number: calculatedPercentage.value,
            duration: .5,
            onUpdate: () => {
                displayValue.value = Number(tweenedNumber.number.toFixed(0));
            }
        });
    };

    const isFull = computed(() => {
        return displayValue.value >= 100;
    });

    onMounted(() => {
        tweenToValue();
    });

    watch(() => props.value,  (newValue) => {
        tweenToValue()
    });
</script>

<template>
    <div class="statBar" :class="{ 'is-full' : isFull }">
        <label>{{ props.label }}</label>

        <div class="statBar__wrapper">
            <div class="statBar__inner" :style="{ width: barStyledObject || 0}">
                <span>{{ displayValue }}% </span>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    $border-radius: 25px;
    $label-spacing: 2px;

    .statBar {
        color: var(--body-text-color);
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        label {
            margin-bottom: $label-spacing;
            color: var(--body-text-color);
            font-weight: 450;
        }

        &__wrapper {
            width: 100%;
            height: 15px;
            background-color: var(--darkened-background);
            border-radius: $border-radius;
            position: relative;
        }

        &__inner {
            height: 100%;
            background-color: var(--primary-color);
            border-radius: $border-radius;
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            will-change: width;
            transition: width 0.5s;
            transition-property: all;
            transition-duration: 1s;

            max-width: 100%;

            /*
            background-image: url('/src/assets/confetti-bg.png');
            animation-name: backgroundMove;
            animation-duration: 15s;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            */

            > span {
                position: absolute;
                right: 0;
                opacity: 0;
                font-size: 14px;
                transition: opacity;
                // transform: translate(125%, calc(-100% - $label-spacing));
                top: 50%;
                transform: translate(125%, -50%);
                font-weight: 400;
                animation: fadeIn 1.5s 0.5s forwards;
            }
        }

        &.is-full .statBar__inner > span:before {
            content: '';
            position: absolute;
            transform: translate(-35%, 0);
            bottom: 80%;
            width: 100px;
            height: 30px;
            background-image: url('/src/assets/confetti-small.gif');
            background-size: cover;
            background-position: 50% 0;
        }
    } 

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes backgroundMove {
        from {
            background-position: 0% 0%;
        }
        to { 
            background-position: 0% 500%;
        }
    }
</style>

